import { Injectable, Injector } from '@angular/core';
import { AdnRow, SessionItemsProvider } from './sessionItems-provider';
import { CurrentUserRolesProvider } from '../../organizations/providers/current-user-roles-provider';
import { mapSessionRightsToRowRights, SessionItemRightsList, SessionRights } from './session-rights';
import { forkJoin, Observable, of, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UtilsProvider } from "../../common/providers/utils-provider.service";
import { ApiBaseService } from "../../common/providers/api-base-service";
import { LangsProvider } from "../../common/providers/langs-provider";
import { CurrentUser } from "../../common/providers/current-user";
import { Session, SessionData } from "../../common/models/session";
import { User } from "../../common/models/user";
import { Organization } from "../../common/models/organization";
import {
  TableHistogramColumn,
  TableHistogramRow,
  TableHistogramValue
} from "../../common/components/table-histogram-chart/table-histogram-chart";
import { Paginator } from '../../common/models/paginator';
import { catchError, map } from "rxjs/operators";
import { TimeslotsProvider } from "../../agendas/providers/timeslots-provider";
import { Athlete } from "../../common/models/athlete";
import { AthleteCoach } from "../../common/models/athlete-coach";
import { AgendaProvider } from "../../agendas/providers/agenda-provider";
import { AthletesProvider } from "../../athletes/providers/athletes-provider";
import { LibraryService } from "../../libraries/providers/library.services";
import { CoachesProvider } from "../../coaches/providers/coaches-provider";
import { GuestsProvider } from "../../guests/providers/guests-provider";
import { OrganizationsProvider } from "../../organizations/providers/organizations-provider";

@Injectable({providedIn: 'root'})
export class SessionsProvider {
  public readonly SESSION_EXERCICE_PAYLOAD_UNIT: string = 'kg';
  private sessionRights: SessionRights = new SessionRights(this._utils);

  private _timeslotsProvider: TimeslotsProvider | null = null;
  private get timeslotsProvider(): TimeslotsProvider {
    if (!this._timeslotsProvider) {
      this._timeslotsProvider = this.injector.get(TimeslotsProvider);
    }
    return this._timeslotsProvider;
  }
  constructor(
    private injector: Injector,
    private _utils: UtilsProvider,
    private api: ApiBaseService,
    private _langs: LangsProvider,
    private _currentUser: CurrentUser,
    private _sessionItemsProvider: SessionItemsProvider,
    private _currentUserRoles: CurrentUserRolesProvider,
    private agendasProvider: AgendaProvider,
    private athletesProvider: AthletesProvider,
    private librariesProvider: LibraryService,
    private coachesProvider: CoachesProvider,
  ) {
  }

  public getSession({id, withTrashed = false, filled = false}: {id: number, withTrashed?: boolean, filled?: boolean}): Observable<Session> {
    const params = {
      with_trashed: withTrashed ? 'true' : 'false',
    }
    const url = filled ? `sessions/${id}/expanded` : `sessions/${id}`;
    return this.api.call('get', url, params, this._currentUser.getAccessToken()).pipe(
      map((data: SessionData) => Session.fromAPI(data))
    );
  }

  public getSessionsByLibraryId(libraryId: number): Observable<Session[]> {
    return this.api.call('get', 'sessions/by-library/' + libraryId, {}, this._currentUser.getAccessToken()).pipe(
      map((data: SessionData[]) => Session.fromAPIList(data))
    );
  }
  public getSessionByTimeslotId(timeslotId: number): Observable<Session> {
    return this.api.call('get', 'sessions/by-timeslot/' + timeslotId, {}, this._currentUser.getAccessToken());
  }

  public getUsersBySessions(sessionIds: number[]): Observable<User[]> {
    return this.api.call('get', `users/by-session/[${sessionIds}]`,{}, this._currentUser.getAccessToken()).pipe(
      map((data: User[]) => User.fromAPIList(data))
    );
    // return this.api.call('get', 'users/by-session/', {sessionIds}, this._currentUser.getAccessToken()).toPromise();
  }

  public listSessionRows(organization: Organization, libraryId: number, reloadAdnInfos: boolean, loadSessionRights = true): Observable<SessionRow[]> {
    return this.getSessionsByLibraryId(libraryId).pipe(
      switchMap((sessions: Session[]) => {
        return this.getSessionRowsFromSessions(organization, sessions, reloadAdnInfos);
      }),
    );
  }


  public listSessionRowsPaginated(organization: Organization, libraryId: number, reloadAdnInfos: boolean, loadSessionRights = true, page = 1, itemsPerPage: number = environment.itemsPerPage): Observable<SessionRow[]> {
    return this.getSessionsByLibraryId(libraryId).pipe(
      switchMap((sessions: Session[]) => {
          return this.getSessionRowsFromSessions(organization, sessions, reloadAdnInfos);
        }
      ));
  }

  public getSessionRowsFromSessions(organization: Organization, sessions: Session[], reloadAdnInfos: boolean): Observable<SessionRow[]> {
    const sessionIds = sessions.map((session) => session.id!);
    return this.getUsersBySessions(sessionIds).pipe(
      map((creaters: User[]) => {
        const rows: SessionRow[] = [];
        sessions.forEach((session) => {
          let tmp: SessionRow;
          tmp = this.toSessionRow(session, this._utils.findIn(session.createrId, creaters, 'id'));

          this.setAdnInfos(organization, session.id!, tmp, reloadAdnInfos);
          rows.push(tmp);
        });

        return rows;
      }),
    );
  }

  public createSessionRow(organization: Organization, session: Session, libraryId: number | undefined, reloadAdnInfos = true): SessionRow {

    const toReturn = this.toSessionRow(session, session.creater ?? {} as User);
    this.setAdnInfos(organization, session.id!, toReturn, reloadAdnInfos);

    return toReturn;
  }

  public getSessionRow({organization, sessionId, session, libraryId, reloadAdnInfos = true, filled = false}: {
    organization: Organization, sessionId?: number, session?: Session, libraryId?: number, reloadAdnInfos?: boolean, filled?: boolean}): Observable<SessionRow> {
    if (!sessionId && !session) {
      throw new Error('Session ou SessionId obligatoire');
    }

    if (sessionId === 0) {
      return of(this.getEmptySessionRow(libraryId));
    }
    // Si une session est passée en params, on l'utilise directement
    if (session) {
      if (!organization.id) {
        throw new Error('organization_id_not_found');
      }
      return of(this.createSessionRow(organization, session, libraryId, reloadAdnInfos));
    }
    // Sinon, on s'assure qu'on a bien un sessionId pour la récupération
    return this.getSession({id: sessionId!, filled: filled}).pipe(
      map((session) => {
        if (session) {
          if (!organization.id) throw new Error('organization_id_not_found');
          return this.createSessionRow(organization, session, libraryId, reloadAdnInfos);
        } else {
          throw new Error('session_not_existe');
        }
      }),
    );
  }

  public rebuildADN(organization: Organization, sessionRow: SessionRow): Promise<any> {
    return new Promise((resolve, reject) => {

      Promise.all([
        this._sessionItemsProvider.listSessionItemRows(organization, sessionRow.id!)
      ])
        .then((data: any[]) => {

          sessionRow.adn = this._sessionItemsProvider.getAdnInfos(data[0], 'timelined', sessionRow.nbRepeat);
          sessionRow.histogramData = this.getHistogramData(sessionRow);

          resolve(null);
        })
        .catch(reject);

      resolve(null);
    });
  }

  public setAdnInfos(organization: Organization, sessionId: number, tmpSessionRow: SessionRow, reloadAdnInfos = true): Promise<any> {
    return new Promise((resolve, reject) => {
      tmpSessionRow.histogramData = this.getHistogramData(tmpSessionRow);
      resolve(null);
    });
  }

  /**
   * Conflit lors du merge : à checker de ton côté
   */
  public getHistogramData(session: SessionRow|any): HistogramData | null{
    return this._sessionItemsProvider.getHistogramData(session);
  }

  // public duplicateIntoLibrary(organization: Organization, sessionId: number): Promise<Session> {
  //     return new Promise<Session>((resolve, reject) => {
  //         this._storage.select(Library)
  //             .where('ownerOrganizationId').equals(organization.id)
  //             .and('userId', OrganizationMembership).equals(this._currentUser.user.id)
  //             .get(1)
  //             .then((library: Library) => {
  //                 Promise.all([
  //                     this.getSessionRow(organization, sessionId, null),
  //                     this._sessionItemsProvider.listSessionItemRows(organization, sessionId),
  //                 ]).then((data: any[]) => {
  //
  //                         const sessionRow = data[0];
  //                         const sessionItems = data[1];
  //
  //                         //"libraryfy" the sessionRow
  //                         sessionRow.id = 0;
  //                         sessionRow.libraryId = library.id;
  //                         sessionRow.type = Session.SESSION_TYPE_MODEL;
  //                         sessionRow.creater = null;
  //                         sessionRow.rights = null;
  //
  //                         //"libraryfy" the sessionItems
  //                         for (let i: number = 0; i < sessionItems.length; i++) {
  //                             this.neutralizeSessionItem(sessionItems[i]);
  //                         }
  //
  //                         this._sessionItemsProvider.saveSessionItemRows(organization.id, 0, sessionItems, sessionRow)
  //                             .then((sessionRow) => {
  //                                 resolve(sessionRow);
  //                             })
  //                             .catch((res) => {
  //                                 reject(res);
  //                             });
  //                     })
  //                     .catch(reject);
  //             })
  //             .catch(reject)
  //     });
  // }

  public saveSessionRow(organization: Organization, sessionRow: SessionRow): Observable<SessionRow> {
    return this.api.call(                'post',
      'organizations/' + organization.id + '/sessions/' + (sessionRow.id ? sessionRow.id : 0),
      this._utils.buildModelParams([this.toSession(sessionRow)], this._langs.getLangIds())[0],
      this._currentUser.getAccessToken()
    ).pipe(
      map((data: any) => {
        const session = Session.fromAPI(data.session);
        return this.toSessionRow(session, sessionRow.creater ?? {} as User);
      })
    );
  }

  public deleteSessionRow(organizationId: number, sessionRow: SessionRow): Observable<any> {
    return this.api.call(              'delete',
      'organizations/' + organizationId + '/sessions/' + sessionRow.id,
      {},
      this._currentUser.getAccessToken());
  }

  public findAllByLibraryPaginated(libraryId: number, pageNumber = 1, itemsPerPage: number = environment.itemsPerPage, keyword = '', filter?: string | null): Observable<Paginator> {
    return this.api.call('get', 'sessions/by-library/' + libraryId + '/paginated', {
      page: pageNumber,
      per_page: itemsPerPage,
      keyword: keyword,
      ...(filter && { ["filter-key"]: filter }),
      type: "model" // force query les model only
    }, this._currentUser.getAccessToken()).pipe(
      map((result: Paginator) =>  Paginator.fromAPI(result)),
    );
  }

  public findAllByLibraryByUserPaginated(libraryId: number, pageNumber = 1, itemsPerPage: number = environment.itemsPerPage, keyword = ''): Observable<Paginator> {
    return this.api.call('get', 'sessions/by-library/' + libraryId + '/user/paginated', {
      page: pageNumber,
      per_page: itemsPerPage,
      keyword: keyword,
      type: "model" // force query les model only
    }, this._currentUser.getAccessToken());
  }

  private getEmptySessionRow(libraryId: number | undefined): SessionRow {
    return  {
      id: 0,
      libraryId,
      type: Session.SESSION_TYPE_MODEL,
      name: '',
      tags: '',
      totalTime: 0,
      intensity: 50,
      nbRepeat: 1,
      comment: '',
      adn: [],
      creater: null,
      histogramData: null,
      availableLangIds: [],
      rights: undefined,
      libraryTag: undefined
    };
  }

  private toSessionRow(session: Session, creater: User): SessionRow {
    return {
      id: session.id!,
      libraryId: session.libraryId!,
      type: session.type!,
      name: session.name!,
      tags: session.tags!,
      totalTime: session.totalTime!,
      intensity: session.intensity!,
      nbRepeat: session.nbRepeat!,
      comment: session.comment!,
      adn: session.adn ? JSON.parse(session.adn!) : session.adn,
      creater: creater,
      rights: mapSessionRightsToRowRights(session.rights ?? []),
      histogramData: null, // TODO ?,
      availableLangIds: session.availableLangIds,
      libraryTag: session.libraryTag,
    } as SessionRow;
  }

  private toSession(sessionRow: SessionRow): Session {
    return new Session({
      id : sessionRow.id,
      libraryId : sessionRow.libraryId,
      type : sessionRow.type,
      name : sessionRow.name,
      tags : sessionRow.tags,
      nbRepeat : sessionRow.nbRepeat,
      intensity : sessionRow.intensity,
      totalTime : sessionRow.totalTime,
      comment : sessionRow.comment,
      availableLangIds: sessionRow.availableLangIds,
      adn : JSON.stringify(sessionRow.adn),
    });
  }

  // private neutralizeSessionItem(item: any):any {
  //     if (item.type === SessionItemWrapper.TYPE_BLOC) {
  //         if (item.innerElements.length) {
  //             for (let j: number = 0; j < item.innerElements.length; j++) {
  //                 this.neutralizeSessionItem(item.innerElements[j]);
  //             }
  //         }
  //         return this.neutralizeBlocItem(item);
  //     }
  //     if (item.type === SessionItemWrapper.TYPE_REST) {
  //         return this.neutralizeRestItem(item);
  //     }
  //     if (item.type === SessionItemWrapper.TYPE_COMMENT) {
  //         return this.neutralizeCommentItem(item);
  //     }
  //     if (item.type === SessionItem_Exercice.TYPE_EXERCICE) {
  //         return this.neutralizeExerciceItem(item);
  //     }
  // }

  // private neutralizeRestItem(item: any): any {
  //     delete item.data.comment;
  //     delete item.data.createDate;
  //     delete item.data.createrId;
  //     delete item.data.deleteDate;
  //     delete item.data.deleterId;
  //     delete item.data.id;
  //     delete item.data.ord;
  //     delete item.data.nbRepeat;
  //     delete item.data.sessionId;
  //     delete item.data.type;
  //     delete item.data.updateDate;
  //     delete item.data.updaterId;
  //     delete item.data.sessionItemWrapperId;
  //     item.parentId = 0;
  //     item.id = 0;
  //     return item;
  // }

  // private neutralizeCommentItem(item: any): any {
  //     delete item.data.createDate;
  //     delete item.data.createrId;
  //     delete item.data.deleteDate;
  //     delete item.data.deleterId;
  //     delete item.data.id;
  //     delete item.data.nbRepeat;
  //     delete item.data.ord;
  //     delete item.data.restTime;
  //     delete item.data.sessionId;
  //     delete item.data.type;
  //     delete item.data.updateDate;
  //     delete item.data.updaterId;
  //     delete item.data.sessionItemWrapperId;
  //     item.parentId = 0;
  //     item.id = 0;
  //     return item;
  // }

  // private neutralizeBlocItem(item: any):any {
  //     delete item.data.comment;
  //     delete item.data.createDate;
  //     delete item.data.createrId;
  //     delete item.data.deleteDate;
  //     delete item.data.deleterId;
  //     delete item.data.id;
  //     delete item.data.ord;
  //     delete item.data.restTime;
  //     delete item.data.sessionId;
  //     delete item.data.type;
  //     delete item.data.updateDate;
  //     delete item.data.updaterId;
  //     item.id = 0;
  //     return item;
  // }

  // private neutralizeExerciceItem(item: any):any {
  //    this.neutralizeSessionItemExercice(item.data.sessionItemExercice);
  //     item.parentId = 0;
  //     item.id = 0;
  //     return item;
  // }
  //
  // private neutralizeSessionItemExercice(item: any):any {
  //     item.createDate = "";
  //     item.createId = 0;
  //     item.sessionItemWrapperId = 0;
  //     item.updateDate = "";
  //     item.updateId = 0;
  //     item.id = 0;
  //     return item;
  // }
}

export interface SessionRow {
  id?: number;
  libraryId?: number;
  type?: string;
  name?: string;
  tags?: string;
  comment?: string;
  totalTime?: number;
  nbRepeat?: number;
  intensity?: number;
  translations?: any;
  adn: AdnRow[];
  histogramData: HistogramData | null;
  creater: User | null;
  rights?: SessionItemRightsList;
  availableLangIds: number[];
  libraryTag?: string;
}

export interface HistogramData {
  rows: TableHistogramRow[];
  cols: TableHistogramColumn[];
  values: TableHistogramValue[];
}
